import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "sonner";

import Home from "./pages/public/Home";
import Catalog from "./pages/public/Catalog";
import Gemstones from "./pages/public/Gemstones";
import GoldScheme from "./pages/public/GoldScheme";
import ProductDetail from "./pages/public/ProductDetail";
import Cart from "./pages/public/Cart";
import Wishlist from "./pages/public/Wishlist";
import Checkout from "./pages/public/Checkout";
import Astrology from "./pages/public/Astrology";
import Login from "./pages/public/Login";
import { Blogs, BlogDetail } from "./pages/public/Blogs";
import { About, Contact, Stores, GoldPrograms, Exchange } from "./pages/public/StaticPages";
import BudgetMatch, { BudgetMatchCategory } from "./pages/public/BudgetMatch";
import UserDashboard from "./pages/public/UserDashboard";

import StaffLogin from "./pages/staff/StaffLogin";
import StaffLayout from "./pages/staff/StaffLayout";
import StaffDashboard from "./pages/staff/StaffDashboard";
import UsersAdmin from "./pages/staff/UsersAdmin";
import { RatesAdmin, RateHistory } from "./pages/staff/RatesAdmin";
import ProductsAdmin from "./pages/staff/ProductsAdmin";
import BlogsAdmin from "./pages/staff/BlogsAdmin";
import CustomersAdmin from "./pages/staff/CustomersAdmin";
import KarigarAdmin from "./pages/staff/KarigarAdmin";
import SchemesAdmin from "./pages/staff/SchemesAdmin";
import PersonalCatalogAdmin from "./pages/staff/PersonalCatalogAdmin";
import AstrologerInbox from "./pages/staff/AstrologerInbox";
import OrdersAdmin from "./pages/staff/OrdersAdmin";
import LeaveApplication from "./pages/staff/LeaveApplication";
import PayoutsPage from "./pages/staff/PayoutsPage";
import LeaveApprovals from "./pages/staff/LeaveApprovals";
import HomeAndStoryPage from "./pages/staff/HomeAndStoryPage";
import CustomisationsPage from "./pages/staff/CustomisationsPage";
import BillingDeskPage from "./pages/staff/BillingDeskPage";
import InventoryPage from "./pages/staff/InventoryPage";
import SalesAnalyticsPage from "./pages/staff/SalesAnalyticsPage";
import { SalesByPersonPage, SlipsPage } from "./pages/staff/SalesTracking";
import {
  AuditLogPage, AttendancePage, AttendanceAdminPage, SoldProductsPage, GoldSchemeAdminPage, CustomisationInputsPage, ReferralsPage, TargetsPage, EODReportPage, SiteCMSPage,
  CouponsPage, WATemplatesPage, FestivalCRMPage, PredictivePage,
  DashboardPeriodPage, DataMigrationPage, InventoryLedgerPage,
} from "./pages/staff/ExtendedAdmin";
import { MyOrdersPage, MyAccountPage, PrivacyPage } from "./pages/public/CustomerPortal";
import {
  FeatureToggleMatrixPage, WorkflowPage, SchemeRemindersPage,
  StockByStorePage, ClickPackPage, FestivalEnginePage,
} from "./pages/staff/Phase3Admin";
import { RevenueDetailsPage, LockRateBookingsPage, StoresAdminPage } from "./pages/staff/RevenueAndStores";
import ScrollTop from "./components/storefront/ScrollTo";
import ScrollToTop from "./components/storefront/ScrollToTop";

function RazorpayLoader() {
  useEffect(() => {
    if (document.getElementById("razorpay-sdk")) return;
    const s = document.createElement("script");
    s.id = "razorpay-sdk";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);
  return null;
}

function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
      <ScrollTop/>
        <AuthProvider>
          <RazorpayLoader />
          <Toaster position="top-center" richColors />
          <RouteErrorBoundary>
          <Routes>
            {/* Public storefront */}
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/gemstones" element={<Gemstones />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/astrology" element={<Astrology />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/stores" element={<Stores />} />
            <Route path="/gold-programs" element={<GoldPrograms />} />
            <Route path="/gold-scheme" element={<GoldScheme />} />
            <Route path="/exchange" element={<Exchange />} />
            <Route path="/budget-match" element={<BudgetMatch />} />
            <Route path="/budget-match/:category" element={<BudgetMatchCategory />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/login" element={<Login />} />
            <Route path="/my-orders" element={<MyOrdersPage />} />
            <Route path="/account" element={<MyAccountPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Staff CRM */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff" element={<StaffLayout />}>
              <Route index element={<StaffDashboard />} />
              <Route path="revenue" element={<RevenueDetailsPage />} />
              <Route path="lock-rates" element={<LockRateBookingsPage />} />
              <Route path="stores" element={<StoresAdminPage />} />
              <Route path="users" element={<UsersAdmin />} />
              <Route path="rates" element={<RatesAdmin />} />
              <Route path="rates/history" element={<RateHistory />} />
              <Route path="products" element={<ProductsAdmin />} />
              <Route path="blogs" element={<BlogsAdmin />} />
              <Route path="customers" element={<CustomersAdmin />} />
              <Route path="orders" element={<OrdersAdmin />} />
              <Route path="karigar" element={<KarigarAdmin />} />
              <Route path="schemes" element={<SchemesAdmin />} />
              <Route path="personal-catalog" element={<PersonalCatalogAdmin />} />
              <Route path="astrology" element={<AstrologerInbox />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="attendance-records" element={<AttendanceAdminPage />} />
              <Route path="sold-products" element={<SoldProductsPage />} />
              <Route path="gold-scheme" element={<GoldSchemeAdminPage />} />
              <Route path="customisation-designs" element={<CustomisationInputsPage />} />
              <Route path="referrals" element={<ReferralsPage />} />
              <Route path="targets" element={<TargetsPage />} />
              <Route path="eod" element={<EODReportPage />} />
              <Route path="payouts" element={<PayoutsPage />} />
              <Route path="leave" element={<LeaveApplication />} />
              <Route path="leave-approvals" element={<LeaveApprovals />} />
              <Route path="site-cms" element={<SiteCMSPage />} />
              <Route path="home-story" element={<HomeAndStoryPage />} />
              <Route path="sales-by-person" element={<SalesByPersonPage />} />
              <Route path="slips" element={<SlipsPage />} />
              <Route path="customisations" element={<CustomisationsPage />} />
              <Route path="billing" element={<BillingDeskPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="sales-analytics" element={<SalesAnalyticsPage />} />
              <Route path="coupons" element={<CouponsPage />} />
              <Route path="wa-templates" element={<WATemplatesPage />} />
              <Route path="festival" element={<FestivalCRMPage />} />
              <Route path="predictive" element={<PredictivePage />} />
              <Route path="dashboard-period" element={<DashboardPeriodPage />} />
              <Route path="migration" element={<DataMigrationPage />} />
              <Route path="inventory" element={<InventoryLedgerPage />} />
              <Route path="permissions" element={<FeatureToggleMatrixPage />} />
              <Route path="workflow" element={<WorkflowPage />} />
              <Route path="scheme-reminders" element={<SchemeRemindersPage />} />
              <Route path="stock-by-store" element={<StockByStorePage />} />
              <Route path="click-pack" element={<ClickPackPage />} />
              <Route path="festival-engine" element={<FestivalEnginePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </RouteErrorBoundary>
        </AuthProvider>
        <ScrollToTop/>
      </BrowserRouter>
    </div>
  );
}
